/** Blank text and a bare "()" name nothing. */
function cleanLabel(value) {
  const text = String(value ?? "").trim();
  if (!text || text.replace(/\s+/g, "") === "()") return "";
  return text;
}

function firstLabel(...values) {
  for (const value of values) {
    const text = cleanLabel(value);
    if (text) return text;
  }
  return "";
}

const moneyCents = (value) => Math.round(value * 100);

/** A coupon and a promotion of the same name are not the same discount. */
const nameKey = (isCoupon, name) =>
  `${isCoupon ? "coupon" : "promotion"}|${cleanLabel(name)}`;

function toList(value) {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
}

function addId(ids, value) {
  const text = cleanLabel(value).toLowerCase();
  if (text) ids.add(text);
}

function productMatchIds(product) {
  const ids = new Set();
  [
    product?.uuid,
    product?.itemUuid,
    product?.itemId,
    product?.id,
    product?.productId,
    product?.productUuid,
    product?._id,
    product?.externalId,
    product?.internalId,
    product?.menuProductId,
    product?.menuItemId,
    product?.product?._id,
    product?.product?.uuid,
    product?.product?.id,
  ].forEach((value) => addId(ids, value));

  toList(product?.modifier).forEach((group) => {
    addId(ids, group?._id);
    addId(ids, group?.id);
    addId(ids, group?.uuid);
    toList(group?.list).forEach((option) => {
      addId(ids, option?._id);
      addId(ids, option?.id);
      addId(ids, option?.uuid);
      addId(ids, option?.itemUuid);
      addId(ids, option?.productId);
    });
  });
  return ids;
}

function affectedItemId(item) {
  return firstLabel(
    item?.itemUuid,
    item?.itemId,
    item?.uuid,
    item?.productId,
    item?.productUuid,
    item?.id
  ).toLowerCase();
}

function affectedItemsOf(promotion) {
  return toList(promotion?.affectedItems)
    .map((item) => {
      const originalPrice = Math.abs(Number(item?.originalPrice) || 0);
      const adjustedPrice = Math.abs(Number(item?.adjustedPrice) || 0);
      const amount =
        Math.abs(Number(item?.discountAmount) || 0) ||
        Math.max(0, originalPrice - adjustedPrice);
      return {
        id: affectedItemId(item),
        qty: Math.abs(Number(item?.qty ?? item?.quantity) || 0),
        amount,
        originalPrice,
        adjustedPrice,
      };
    })
    .filter((item) => item.amount > 0 || item.id);
}

/**
 * promotionDetails wins when it names the items it touched. A list that only
 * carries the total does not hide appliedPromotions, which is where those
 * orders keep the per-item itemUuid breakdown. An empty array still wins.
 * A coupon that lives only on appliedPromotions is still included, so the
 * item lines are not limited to the automatic promotion.
 */
function promotionList(order) {
  const details = order?.charges?.promotionDetails;
  const applied = toList(order?.promotionInfo?.appliedPromotions);
  if (!Array.isArray(details)) return applied;
  if (!details.length) return details;

  const linked = details.some((promotion) => affectedItemsOf(promotion).length);
  if (!linked) return applied.length ? applied : details;

  const ids = new Set(
    details
      .map((promotion) => cleanLabel(promotion?.promotionUuid).toLowerCase())
      .filter(Boolean)
  );
  const extras = applied.filter((promotion) => {
    const id = cleanLabel(promotion?.promotionUuid).toLowerCase();
    if (id && ids.has(id)) return false;
    const name = cleanLabel(promotion?.promotionName).toLowerCase();
    const amount = Math.abs(Number(promotion?.discountAmount) || 0);
    const same = details.some((detail) => {
      const sameName =
        name && cleanLabel(detail?.promotionName).toLowerCase() === name;
      const sameAmount =
        amount > 0 &&
        moneyCents(amount) ===
          moneyCents(Math.abs(Number(detail?.discountAmount) || 0));
      return sameName && sameAmount;
    });
    return !same;
  });
  return details.concat(extras);
}

function orderCouponName(order) {
  const fromDetails = toList(order?.charges?.discountDetails)
    .map((detail) =>
      firstLabel(
        detail?.couponTitle,
        detail?.couponName,
        detail?.couponCode,
        detail?.discountCode
      )
    )
    .find(Boolean);
  return firstLabel(
    fromDetails,
    order?.promotionInfo?.couponTitle,
    order?.promotionInfo?.couponName,
    order?.promotionInfo?.couponCode
  );
}

/** A coded promotion, or the promotion the order redeemed, is a coupon. */
function isCouponPromotion(promotion, order) {
  if (cleanLabel(promotion?.promotionCode)) return true;
  const redeemed = cleanLabel(order?.promotionInfo?.promotionUuid);
  return !!redeemed && cleanLabel(promotion?.promotionUuid) === redeemed;
}

function promotionName(promotion, order, isCoupon) {
  if (!isCoupon) {
    return firstLabel(promotion?.promotionName, promotion?.displayLabel);
  }
  return firstLabel(
    promotion?.displayLabel,
    promotion?.promotionName,
    order?.promotionInfo?.couponTitle,
    order?.promotionInfo?.couponName,
    promotion?.promotionCode
  );
}

function toChargeRecord(promotion, index, order) {
  const affected = affectedItemsOf(promotion);
  const amount =
    Math.abs(Number(promotion?.discountAmount) || 0) ||
    affected.reduce((sum, item) => sum + item.amount, 0);
  if (!amount) return null;

  const isCoupon = isCouponPromotion(promotion, order);
  return {
    key: cleanLabel(promotion?.promotionUuid) || `promotion-${index}`,
    isCoupon,
    name: promotionName(promotion, order, isCoupon),
    amount,
    affected,
  };
}

function promotionChargeRecords(order) {
  const records = promotionList(order)
    .map((promotion, index) => toChargeRecord(promotion, index, order))
    .filter(Boolean);

  const covered = new Set(
    records.flatMap((record) => record.affected.map((item) => item.id))
  );

  toList(order?.charges?.discountDetails).forEach((detail, index) => {
    if (
      String(detail?.appliesTo || "").toLowerCase() === "order" ||
      detail?.isCashDiscount ||
      detail?.freeDelivery
    ) {
      return;
    }
    const itemId = cleanLabel(detail?.itemUuid).toLowerCase();
    if (!itemId || covered.has(itemId)) return;

    const couponId = cleanLabel(detail?.couponId);
    const name = firstLabel(
      detail?.couponTitle,
      detail?.couponName,
      detail?.couponCode,
      detail?.discountCode
    );
    if (!couponId && !name) return;

    const amount = Math.abs(Number(detail?.discountAmount) || 0);
    if (!amount) return;

    records.push({
      key: couponId || `item-coupon-${itemId}-${index}`,
      isCoupon: true,
      name,
      amount,
      affected: [{ id: itemId, amount, qty: 0, originalPrice: 0, adjustedPrice: 0 }],
    });
  });

  const couponFromRecords = records
    .filter((record) => record.isCoupon)
    .reduce((sum, record) => sum + record.amount, 0);
  const couponAmount = Math.abs(Number(order?.charges?.couponAmount) || 0);
  if (!couponFromRecords && couponAmount) {
    records.push({
      key: "coupon-amount",
      isCoupon: true,
      name: orderCouponName(order),
      amount: couponAmount,
      affected: [],
    });
  }

  return records;
}

/**
 * Assigns each discounted line to one item. Id match is preferred. When the
 * line only states the price before the discount, the item is matched by that
 * money instead. Two items at the same price each take the next free match.
 */
export function automaticPromotionRows(order) {
  return promotionChargeRecords(order).filter((record) => !record.isCoupon);
}

function lineQty(product) {
  return Math.abs(Number(product?.quantity ?? product?.count) || 0) || 1;
}

function modifierTotal(product) {
  return toList(product?.modifier).reduce((sum, group) => {
    return (
      sum +
      toList(group?.list).reduce((inner, option) => {
        if (option?.isFree) return inner;
        const count = Math.abs(Number(option?.count) || 0) || 1;
        const free = Math.abs(Number(option?.freeCount) || 0);
        const paid = Math.max(0, count - free);
        return inner + paid * Math.abs(Number(option?.price) || 0);
      }, 0)
    );
  }, 0);
}

/** Prices a promotion line might have recorded for this product. */
function lineAmounts(product) {
  const qty = lineQty(product);
  const unit = Math.abs(Number(product?.price) || 0);
  const extended = unit * qty;
  const withModifiers = extended + modifierTotal(product);
  const total = Math.abs(Number(product?.total) || 0);
  return [extended, withModifiers, total, unit].filter(
    (value, index, all) => value > 0 && all.indexOf(value) === index
  );
}

function sameMoney(left, right) {
  return moneyCents(left) === moneyCents(right);
}

function priceMatches(product, originalPrice) {
  if (!originalPrice) return false;
  return lineAmounts(product).some((amount) => sameMoney(amount, originalPrice));
}

/**
 * affectedItems.itemUuid is the menu item. An order can have several lines of
 * that item, so quantity and the pre-discount price pick the line. A price
 * match is only used when the id is not on any product.
 */
function matchProductIndex(products, item, used) {
  const free = (index) => !used.has(index);

  if (item.id) {
    const byLineId = products.findIndex(
      (product, index) =>
        free(index) &&
        [product?.uuid, product?._id].some(
          (value) => cleanLabel(value).toLowerCase() === item.id
        )
    );
    if (byLineId >= 0) return byLineId;

    const byMenuId = products
      .map((product, index) => ({ product, index }))
      .filter(
        ({ product, index }) => free(index) && productMatchIds(product).has(item.id)
      );

    if (byMenuId.length === 1) return byMenuId[0].index;
    if (byMenuId.length > 1) {
      const byQtyAndPrice = byMenuId.find(({ product }) => {
        const qtyOk = !item.qty || lineQty(product) === item.qty;
        const priceOk =
          !item.originalPrice || priceMatches(product, item.originalPrice);
        return qtyOk && priceOk;
      });
      if (byQtyAndPrice) return byQtyAndPrice.index;
      const byQty = byMenuId.find(
        ({ product }) => item.qty && lineQty(product) === item.qty
      );
      if (byQty) return byQty.index;
      return byMenuId[0].index;
    }
  }

  if (!item.originalPrice) return -1;
  return products.findIndex(
    (product, index) => free(index) && priceMatches(product, item.originalPrice)
  );
}

/** Splits cents by weight so the shares add back up to the total. */
function splitCents(totalCents, weights) {
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  if (totalCents <= 0 || totalWeight <= 0) return weights.map(() => 0);
  const parts = weights.map((weight) => {
    const raw = (totalCents * weight) / totalWeight;
    const cents = Math.floor(raw + 1e-6);
    return { cents, fraction: raw - cents };
  });
  let remainder = totalCents - parts.reduce((sum, part) => sum + part.cents, 0);
  parts
    .map((part, index) => ({ index, fraction: part.fraction }))
    .sort((left, right) => right.fraction - left.fraction || left.index - right.index)
    .forEach((part) => {
      if (remainder <= 0) return;
      parts[part.index].cents += 1;
      remainder -= 1;
    });
  return parts.map((part) => part.cents / 100);
}

export function createItemPromotionLookup(order) {
  const records = promotionChargeRecords(order);
  const products = toList(order?.products);
  const linesByIndex = products.map(() => []);
  const printedNames = new Set();

  const pushLine = (record, productIndex, suffix, amount) => {
    const share = Number(amount) || 0;
    if (productIndex < 0 || share <= 0) return false;
    if (record.name) printedNames.add(nameKey(record.isCoupon, record.name));
    const bucket = linesByIndex[productIndex];
    if (!bucket) return false;
    bucket.push({
      key: `${record.key}:${suffix}`,
      isCoupon: record.isCoupon,
      name: record.name,
      amount: share,
    });
    return true;
  };

  const lineBase = (product) => lineAmounts(product)[0] || 0;
  const appliedOn = (index) =>
    (linesByIndex[index] || []).reduce(
      (sum, line) => sum + (Number(line.amount) || 0),
      0
    );
  const remainingAt = (index) =>
    Math.max(0, lineBase(products[index]) - appliedOn(index));

  records.forEach((record) => {
    const used = new Set();
    let placed = 0;
    const missed = [];

    record.affected.forEach((item, affectedIndex) => {
      const productIndex = matchProductIndex(products, item, used);
      if (productIndex < 0) {
        missed.push({ item, affectedIndex });
        return;
      }
      used.add(productIndex);
      const amount =
        item.amount || (record.affected.length === 1 ? record.amount : 0);
      if (pushLine(record, productIndex, affectedIndex, amount)) placed += 1;
    });

    // A coupon taken after the promotion quotes the price that is left.
    if (record.isCoupon) {
      missed.forEach(({ item, affectedIndex }) => {
        if (!item.originalPrice) return;
        const productIndex = products.findIndex(
          (_, index) =>
            !used.has(index) &&
            moneyCents(remainingAt(index)) === moneyCents(item.originalPrice)
        );
        if (productIndex < 0) return;
        used.add(productIndex);
        if (pushLine(record, productIndex, affectedIndex, item.amount)) placed += 1;
      });
    }

    // Order-level coupon: the summary already has the total, and each item
    // shows its share. Shares add back up to that total.
    if (!record.isCoupon || placed || !(record.amount > 0) || !products.length) {
      return;
    }

    const weights = products.map(
      (product, index) => remainingAt(index) || lineBase(product)
    );
    splitCents(moneyCents(record.amount), weights).forEach((amount, index) => {
      pushLine(record, index, `share-${index}`, amount);
    });
  });

  return {
    promotionRows: records.filter((record) => !record.isCoupon),
    couponRows: records.filter((record) => record.isCoupon),
    isNamedOnItem(isCoupon, name) {
      const label = cleanLabel(name);
      return !!label && printedNames.has(nameKey(isCoupon, label));
    },
    linesFor(product) {
      const direct = products.indexOf(product);
      if (direct >= 0) return linesByIndex[direct] || [];

      const id = cleanLabel(product?.uuid) || cleanLabel(product?._id);
      const bucket = products.findIndex(
        (item) => id && (cleanLabel(item?.uuid) === id || cleanLabel(item?._id) === id)
      );
      return bucket < 0 ? [] : linesByIndex[bucket] || [];
    },
  };
}

/** "Coupon (name)" / "Promotion (name)", or the bare kind when it has no name. */
export function itemPromotionTitle(kindLabel, name) {
  const label = cleanLabel(name);
  return label ? `${kindLabel} (${label})` : kindLabel;
}
